<template>
  <div
    class="wrapper"
    :class="[
      { 'nav-open': $sidebar.showSidebar },
      { rtl: $route.meta.rtlActive },
    ]"
  >
    <notifications></notifications>

    <!-- Botón de toggle del sidebar -->
    <sidebar-toggle></sidebar-toggle>

    <side-bar
      background-color="info"
      :data-background-color="sidebarBackgroundColor"
    >
      <user-menu :title="userName" :logo="(site && site.logo) || ''"></user-menu>

      <!-- En el teléfono el buscador vive en el encabezado (más abajo). -->
      <sidebar-quick-search
        v-if="!isMobileLayout"
        :active-modules="activeModules"
      />

      <template slot="links">
        <dashboard-menu-branch
          v-for="(br, idx) in effectiveMenuBranches"
          :key="'mb-' + menuBranchKey(br, idx)"
          :branch="br"
        />
      </template>
    </side-bar>
    <div class="main-panel">
      <BillingOverdueTopAlert v-if="!isTemplateRoute" />
      <!-- Teléfono: logo redondo, buscador y menú. -->
      <div v-if="isMobileLayout && !isTemplateRoute" class="m-header">
        <router-link
          to="/gotrading/dashboard"
          class="m-header__logo"
          :aria-label="$t('mobile.home')"
          @click.native="closeSidebarIfOpen"
        >
          <img v-if="site && site.logo" :src="site.logo" :alt="site.siteName" />
        </router-link>
        <sidebar-quick-search
          class="m-header__search"
          in-header
          :active-modules="activeModules"
        />
        <AgentAiTopButton v-if="agentChatEnabled" compact />
        <button
          type="button"
          class="m-header__menu"
          :aria-label="$t('mobile.menu')"
          @click="toggleSidebar"
        >
          <i class="material-icons">{{ $sidebar.showSidebar ? "close" : "menu" }}</i>
        </button>
      </div>
      <div
        v-else
        class="sidebar mobile sidebar-mobile mb-3 cursor-pointer w-100 bg-white p-0 m-0"
        @click="toggleSidebar"
      >
        <div class="logo bg-white text-right mobile-logo-bar">
          <span class="simple-text logotel">
            <img :src="site.logo" width="30" height="30" />
            <span class="text-black p-2 font-weight-bold">{{
              site.siteName
            }}</span>
          </span>
          <span class="text-right menu-container">
            <button class="md-button md-info md-simple md-just-icon">
              <i class="material-icons" v-if="!$sidebar.showSidebar">menu</i>
              <i class="material-icons" v-if="$sidebar.showSidebar">close</i>
            </button>
          </span>
        </div>
      </div>

      <!-- Teléfono desde el navegador: recomendar instalar la app (se puede cerrar). -->
      <MobileInstallBanner
        v-if="isMobileLayout && !isTemplateRoute && site"
        :name="site.siteName || ''"
        :logo="site.logo || ''"
      />
      <!-- App instalada sin notificaciones: tarjeta fija para activarlas. -->
      <MobilePushBanner v-if="isMobileLayout && !isTemplateRoute" />
      <!-- El icono de la IA flota arriba a la derecha sin reservarse lugar: el contenido usa todo el
           ancho (una franja de 72 px a lo alto de toda la pantalla era espacio perdido). -->
      <div>
        <!-- En las pantallas de modelos el icono de la IA va en la línea del breadcrumb, sin flotar. -->
        <div v-if="!isTemplateRoute && aiTopInBreadcrumb" class="breadcrumb-row">
          <Breadcrumb class="breadcrumb-row__crumbs" />
          <AgentAiTopButton v-if="agentChatEnabled" inline class="breadcrumb-row__ai" />
        </div>
        <Breadcrumb v-else-if="!isTemplateRoute" />
        <zoom-center-transition :duration="200" mode="out-in">
          <!-- your content here -->
          <router-view></router-view>
        </zoom-center-transition>
      </div>
      <content-footer v-if="!$route.meta.hideFooter"></content-footer>
      <!-- Escritorio: burbuja del Agente IA (solo si el usuario lo tiene; en el teléfono está /chat). -->
      <AgentChatFab v-if="agentChatEnabled && !isMobileLayout && !isTemplateRoute" />
      <!-- Escritorio: el icono de la IA flotando arriba a la derecha (lleva a /ia y de él salen
           los consejos del día). En el dashboard va en su barra, al lado de la campana. -->
      <AgentAiTopButton v-if="agentChatEnabled && !isMobileLayout && !isTemplateRoute && !isDashboardRoute && !aiTopInBreadcrumb" />
      <!-- Teléfono: barra de navegación inferior (components/mobile). -->
      <MobileBottomNav
        v-if="isMobileLayout && !isTemplateRoute"
        :items="mobileBottomNavItems"
        :label="$t('mobile.navigation')"
        @select="onMobileBottomNav"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable no-new */
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import permissionsMixin from "@/mixins/permissionsMixin";
import mobileLayoutMixin from "@/mixins/mobileLayoutMixin";
import MobileBottomNav from "@/components/mobile/MobileBottomNav.vue";
import MobileInstallBanner from "@/components/mobile/MobileInstallBanner.vue";
import MobilePushBanner from "@/components/mobile/MobilePushBanner.vue";
import Breadcrumb from "@/components/gotrading/common/Breadcrumb.vue";

import ContentFooter from "./ContentFooter.vue";
import UserMenu from "./Extra/UserMenu.vue";
import SidebarQuickSearch from "./Extra/SidebarQuickSearch.vue";
import { ZoomCenterTransition } from "vue2-transitions";

import { wencesApi } from "@/api/wencesApi";
import {
  mergeMissingBuiltins,
  isMenuFixedTopRef,
  isMenuFixedBottomRef,
  buildDefaultMenuRawRoots,
  stripHiddenModelsFromRawRoots,
  appendMissingReportsToModelNodes,
  reportsOfModel,
} from "@/utils/userMenuLayoutMerge";
import DashboardMenuBranch from "./DashboardMenuBranch.vue";
import BillingOverdueTopAlert from "@/components/billing/BillingOverdueTopAlert.vue";
import { isTenantTestEnvironment } from "@/utils/tenantTestEnvironment";
import {
  cronjobsModuleMenuItem,
  CRONJOBS_MODULE_NAME,
} from "@/modules/cronjobs_module";
import {
  comexstatsModuleMenuItem,
  COMEXSTATS_MODULE_NAME,
} from "@/modules/comexstats_module";
import {
  historyModuleMenuItem,
  HISTORY_MODULE_NAME,
} from "@/modules/history_module";
import {
  fobtradingModuleMenuItem,
  FOBTRADING_MODULE_NAME,
} from "@/modules/fobtrading_module";
import { mevModuleMenuItem, MEV_MODULE_NAME } from "@/modules/mev_module";
import { outlookModuleMenuItem, OUTLOOK_MODULE_NAME } from "@/modules/outlook_module";
import {
  notaComercialModuleMenuItem,
  NOTA_COMERCIAL_MODULE_NAME,
} from "@/modules/nota_comercial_module";
import {
  alisofModuleMenuItem,
  alisofPosicionesMenuItem,
  ALISOF_MODULE_NAME,
} from "@/modules/alisof_module";
import {
  bolsaComercialesModuleMenuItem,
  BOLSA_COMERCIALES_MODULE_NAME,
} from "@/modules/bolsa_comerciales_module";
import {
  facturacionAbogadosModuleMenuItem,
  FACTURACION_ABOGADOS_MODULE_NAME,
} from "@/modules/facturacion_abogados_module";
import {
  JURISPRUDENCIA_MODULE_NAME,
  jurisprudenciaModuleMenuItem,
} from "@/modules/jurisprudencia_module";
import { ARCA_MODULE_NAME, arcaModuleMenuItem } from "@/modules/arca_module";
import {
  TELEGRAM_AI_MODULE_NAME,
  agentChatMenuItem,
} from "@/modules/telegram_ai_module";
import {
  INMOBILIARIA_MODULE_NAME,
  inmobiliariaModuleMenuItem,
} from "@/modules/inmobiliaria_module";

export default {
  mixins: [permissionsMixin, mobileLayoutMixin],
  components: {
    MobileBottomNav,
    MobileInstallBanner,
    MobilePushBanner,
    ContentFooter,
    UserMenu,
    SidebarQuickSearch,
    ZoomCenterTransition,
    Breadcrumb,
    DashboardMenuBranch,
    BillingOverdueTopAlert,
    AgentChatFab: () => import("@/modules/telegram_ai_module/components/AgentChatFab.vue"),
    AgentAiTopButton: () => import("@/modules/telegram_ai_module/components/AgentAiTopButton.vue"),
  },
  provide() {
    return { dashboardLayout: this };
  },
  computed: {
    /** Accesos de la barra inferior del teléfono (mismos permisos que el menú). */
    mobileBottomNavItems() {
      const path = (this.$route && this.$route.path) || "";
      const items = [
        {
          key: "home",
          label: this.$t("mobile.home"),
          icon: "home",
          to: "/gotrading/dashboard",
          active: !this.$sidebar.showSidebar && path.startsWith("/gotrading/dashboard"),
        },
      ];
      if (this.canAccessAgentChat) {
        items.push({
          key: "agent",
          label: this.$t("ai.agentChatTitle"),
          icon: "comments",
          to: "/chat",
          active: false,
        });
      }
      if (this.canAccessSupportModule) {
        items.push({
          key: "support",
          label: this.$t("mobile.support"),
          icon: "headset",
          to: "/support/tickets",
          active: !this.$sidebar.showSidebar && path.startsWith("/support"),
          badge: this.supportUnreadMentions > 0 ? String(this.supportUnreadMentions) : "",
        });
      }
      items.push({
        key: "menu",
        label: this.$t("mobile.menu"),
        icon: "bars",
        active: !!this.$sidebar.showSidebar,
      });
      return items;
    },
    /** El dashboard y los listados de modelos dibujan el icono de la IA en su propia barra. */
    isDashboardRoute() {
      return [
        "Dashboard",
        "GoTradingDashboard",
        "DynamicModel",
        "DynamicModelTrash",
        "DynamicModelArchive",
      ].includes(this.$route?.name);
    },
    /** Escritorio, el resto de las pantallas de modelos (fichas): el icono de la IA va al lado del breadcrumb. */
    aiTopInBreadcrumb() {
      return (
        !this.isMobileLayout &&
        !this.isDashboardRoute &&
        (this.$route?.path || "").startsWith("/dynamic-model/")
      );
    },
    isTemplateRoute() {
      return this.$route?.path?.startsWith("/templates/") || false;
    },
    defaultMenuRawRoots() {
      return buildDefaultMenuRawRoots({
        filteredModels: this.filteredModels,
        getModelReports: (mn) => this.getModelReports(mn),
        canAccessStockModule: this.canAccessStockModule,
        canAccessFinancialModuleWithPermissions:
          this.canAccessFinancialModuleWithPermissions,
        canAccessAccountingModule: this.canAccessAccountingModule,
        canAccessDHLModule: this.canAccessDHLModule,
        canAccessFindTeuModule: this.canAccessFindTeuModule,
        canAccessComexstatsModule: this.canAccessComexstatsModule,
        canAccessHistoryModule: this.canAccessHistoryModule,
        canAccessFobtradingModule: this.canAccessFobtradingModule,
        canAccessMevModule: this.canAccessMevModule,
        canAccessOutlookModule: this.canAccessOutlookModule,
        canAccessNotaComercialModule: this.canAccessNotaComercialModule,
        canAccessAlisofModule: this.canAccessAlisofModule,
        canAccessBolsaComercialesModule: this.canAccessBolsaComercialesModule,
        canAccessBolsaComercialesDashboard:
          this.canAccessBolsaComercialesDashboard,
        canAccessBolsaComercialesSettings:
          this.canAccessBolsaComercialesSettings,
        canAccessFacturacionAbogadosModule:
          this.canAccessFacturacionAbogadosModule,
        canAccessJurisprudenciaModule: this.canAccessJurisprudenciaModule,
        canAccessArcaModule: this.canAccessArcaModule,
        canAccessAgentChat: this.canAccessAgentChat,
        canAccessInmobiliariaModule: this.canAccessInmobiliariaModule,
        canAccessExcelMappingModule: this.canAccessExcelMappingModule,
        canAccessWebsiteModule: this.canAccessWebsiteModule,
        canAccessEmailModule: this.canAccessEmailModule,
        canAccessTranslationsModule: this.canAccessTranslationsModule,
        canAccessSupportModule: this.canAccessSupportModule,
        canAccessInstanceBuilderModule: this.canAccessInstanceBuilderModule,
        canAccessTelegramAiModule: this.canAccessTelegramAiModule,
        isChatModuleActive: this.isChatModuleActive,
        isCronjobsModuleActive: this.isCronjobsModuleActive,
        isAdminRole: this.isAdminRole,
        canAccessSettingsSystemConfig: this.hasPermission("settings_manage"),
        canAccessSettingsModelReferenceMapping: this.hasPermission(
          "model-definitions.view"
        ),
        canAccessSettingsModelDefinitions: this.hasPermission(
          "model-definitions.view"
        ),
        canAccessSettingsHookAutomations: this.isAdminRole,
        canAccessSettingsViews: this.hasPermission("views.view"),
        canAccessSettingsRoles: this.hasPermission("roles.view"),
        canAccessSettingsUsers: this.canAccessUsersSettings(),
        canAccessSettingsLists: this.hasPermission("lists.view"),
        canAccessSettingsAdminLinks: this.hasPermission("views.view"),
        canAccessSettingsModules: this.hasPermission("settings_manage"),
        canAccessSettingsRelationships: this.hasPermission("settings_manage"),
        canAccessSettingsPromptSettings: this.hasPermission("settings_manage"),
        canAccessSettingsOrphans: this.isAdminRole,
      });
    },
    effectiveMenuBranches() {
      const out = this.effectiveMenuBranchesFromLayout;
      // Soporte técnico va siempre último, diga lo que diga "Mi menú"
      if (!this.canAccessSupportModule) return out;
      return [...out, { kind: "builtin", ref: "nav:support" }];
    },
    effectiveMenuBranchesFromLayout() {
      const rawFromUser = this.userMenuLayoutMerged;
      const useUserRoots =
        Array.isArray(rawFromUser) && rawFromUser.length > 0;
      const seed = useUserRoots ? rawFromUser : this.defaultMenuRawRoots;
      const mergedBase = mergeMissingBuiltins(seed, this.defaultMenuRawRoots);
      const mergedWithReports = useUserRoots
        ? mergedBase
        : appendMissingReportsToModelNodes(
            mergedBase,
            this.reports,
            this.filteredModels
          );
      const merged = stripHiddenModelsFromRawRoots(
        mergedWithReports,
        this.models
      );
      const folderMap = new Map(
        (this.userMenuFolders || []).map((f) => [String(f._id), f])
      );
      const toBranch = (n) => {
        if (!n) return null;
        if (n.t === "u" && n.u != null && String(n.u).trim() && n.l != null) {
          const lab = String(n.l).trim();
          if (!lab) return null;
          const href = String(n.u).trim();
          if (!href) return null;
          return {
            kind: "customLink",
            href,
            label: lab,
            icon:
              n.i != null && String(n.i).trim()
                ? String(n.i).trim()
                : "link",
            cid:
              n.cid != null && String(n.cid).trim()
                ? String(n.cid).trim()
                : "",
          };
        }
        if (n.t === "b" && n.r) {
          const rawKids = Array.isArray(n.c) ? n.c : [];
          const children = rawKids.map(toBranch).filter(Boolean);
          const b = { kind: "builtin", ref: n.r };
          if (n.l != null && String(n.l).trim()) {
            b.menuLabel = String(n.l).trim();
          }
          if (n.r.startsWith("model:") && (n.h === 1 || n.h === true)) {
            b.hideAddNew = true;
          }
          if (n.r.startsWith("model:") && children.length) {
            b.children = children;
          }
          return b;
        }
        if (n.t === "f" && n.id) {
          const f = folderMap.get(String(n.id));
          if (!f) return null;
          const children = (n.c || []).map(toBranch).filter(Boolean);
          return {
            kind: "folder",
            id: String(n.id),
            label: f.label,
            icon: f.icon || "folder",
            children,
          };
        }
        return null;
      };
      const isRootHoistableSettingsRaw = (n) =>
        n &&
        n.t === "b" &&
        typeof n.r === "string" &&
        n.r.startsWith("settings:") &&
        n.r !== "settings:none" &&
        n.r !== "settings:user_menu";

      let splitIdx = merged.length;
      while (splitIdx > 0 && isRootHoistableSettingsRaw(merged[splitIdx - 1])) {
        splitIdx--;
      }
      const rootPrefixRaw = merged.slice(0, splitIdx);
      const rootSettingsSuffixRaw = merged.slice(splitIdx);
      const allSettingsRootsAreTrailingSuffix =
        rootSettingsSuffixRaw.length > 0 &&
        !rootPrefixRaw.some(isRootHoistableSettingsRaw);

      const hasAnySettingsRef = (() => {
        const walk = (nodes) => {
          if (!Array.isArray(nodes)) return false;
          for (const n of nodes) {
            if (!n) continue;
            if (
              n.t === "b" &&
              typeof n.r === "string" &&
              n.r.startsWith("settings:") &&
              n.r !== "settings:none"
            ) {
              return true;
            }
            if (n.t === "b" && Array.isArray(n.c) && walk(n.c)) return true;
            if (n.t === "f" && Array.isArray(n.c) && walk(n.c)) return true;
          }
          return false;
        };
        return walk(merged);
      })();

      let settingsNoneMarker = false;
      for (const n of merged) {
        if (n && n.t === "b" && n.r === "settings:none") {
          settingsNoneMarker = true;
          break;
        }
      }

      const settingsChildren =
        rootSettingsSuffixRaw.length > 0
          ? rootSettingsSuffixRaw.map(toBranch).filter(Boolean)
          : [];

      const useLegacySettings =
        !settingsNoneMarker &&
        !hasAnySettingsRef &&
        settingsChildren.length === 0;

      const out = [{ kind: "builtin", ref: "nav:dashboard" }];

      const pushRootBranchUnlessSkip = (rawN) => {
        if (!rawN) return;
        if (rawN.t === "b" && rawN.r === "settings:none") {
          settingsNoneMarker = true;
          return;
        }
        const b = toBranch(rawN);
        if (!b) return;
        if (
          b.kind === "builtin" &&
          b.ref &&
          (isMenuFixedTopRef(b.ref) || isMenuFixedBottomRef(b.ref))
        ) {
          return;
        }
        out.push(b);
      };

      if (useLegacySettings) {
        out.push({
          kind: "builtin",
          ref: "nav:settings",
          children: [],
          legacyBlock: true,
        });
        for (const rawN of merged) {
          pushRootBranchUnlessSkip(rawN);
        }
        return out;
      }

      if (allSettingsRootsAreTrailingSuffix && settingsChildren.length > 0) {
        out.push({
          kind: "builtin",
          ref: "nav:settings",
          children: settingsChildren,
          legacyBlock: false,
        });
        for (const rawN of rootPrefixRaw) {
          pushRootBranchUnlessSkip(rawN);
        }
        return out;
      }

      let k = 0;
      while (k < merged.length) {
        if (
          splitIdx < merged.length &&
          k === splitIdx &&
          settingsChildren.length > 0
        ) {
          out.push({
            kind: "builtin",
            ref: "nav:settings",
            children: settingsChildren,
            legacyBlock: false,
          });
          k += rootSettingsSuffixRaw.length;
          continue;
        }
        pushRootBranchUnlessSkip(merged[k]);
        k++;
      }
      return out;
    },
    isAdminRole() {
      return (
        typeof localStorage !== "undefined" &&
        localStorage.getItem("role") === "admin"
      );
    },
    filteredModels() {
      return this.models
        .filter((model) => {
          if (model.isGroupModel) return false;
          if (model.hideFromMenu) return false; // Ocultar modelos marcados como ocultos del menú
          const order = model.order || 0;
          if (order <= 0) return false;
          const modelName = (model.modelName || "").toLowerCase();
          return this.hasPermission(`${modelName}.view`);
        })
        .sort((a, b) => {
          const orderA = a.order || 0;
          const orderB = b.order || 0;
          return orderA - orderB;
        });
    },
    modelsWithReports() {
      return this.filteredModels.filter(
        (model) => this.getModelReports(model.modelName).length > 0
      );
    },
    modelsWithoutReports() {
      return this.filteredModels.filter(
        (model) => this.getModelReports(model.modelName).length === 0
      );
    },
    /** Modelos sin reportes que tienen al menos un campo group con showInMenu (se muestran con submenú) */
    modelsWithoutReportsWithGroupMenu() {
      return this.modelsWithoutReports.filter(
        (model) => this.getGroupFieldsInMenu(model).length > 0
      );
    },
    /** Modelos sin reportes ni grupos en menú (link directo) */
    modelsWithoutReportsDirectLink() {
      return this.modelsWithoutReports.filter(
        (model) => this.getGroupFieldsInMenu(model).length === 0
      );
    },
    isFinancialModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "financial_module"
      );
    },
    canAccessFinancialModule() {
      // Primero verificar si el módulo está activo - si no está activo, nadie puede acceder
      if (!this.isFinancialModuleActive) {
        return false;
      }

      // Si el usuario es admin, tiene acceso
      if (localStorage.getItem("role") === "admin") {
        return true;
      }

      // Verificar si el rol del usuario tiene acceso al módulo
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }

      // Buscar en los roles cargados si están disponibles
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes("financial_module");
        }
      }

      // Lógica de respaldo para roles específicos
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    // Verifica acceso al módulo finance Y permisos específicos (para el menú /financial)
    canAccessFinancialModuleWithPermissions() {
      // Primero verificar acceso básico al módulo
      if (!this.canAccessFinancialModule) {
        return false;
      }

      // Si el usuario es admin, tiene acceso completo
      if (localStorage.getItem("role") === "admin") {
        return true;
      }

      // Verificar si el usuario tiene permisos específicos de finance
      // Permisos válidos: finance.manage o finance.payments
      const financePermissions = ["finance.manage", "finance.payments"];

      // Verificar si tiene alguno de los permisos de finance
      const hasFinancePermission = financePermissions.some((permission) =>
        this.hasPermission(permission)
      );

      return hasFinancePermission;
    },
    // Verifica acceso al Dashboard de Finance (solo con finance.manage)
    canAccessFinancialDashboard() {
      if (!this.canAccessFinancialModule) return false;
      if (localStorage.getItem("role") === "admin") return true;
      return this.hasPermission("finance.manage");
    },
    // Verifica acceso a Payments (con finance.manage o finance.payments)
    canAccessFinancialPayments() {
      if (!this.canAccessFinancialModule) return false;
      if (localStorage.getItem("role") === "admin") return true;
      return (
        this.hasPermission("finance.manage") ||
        this.hasPermission("finance.payments")
      );
    },
    // Verifica acceso a Expenses (solo con finance.manage)
    canAccessFinancialExpenses() {
      if (!this.canAccessFinancialModule) return false;
      if (localStorage.getItem("role") === "admin") return true;
      return this.hasPermission("finance.manage");
    },
    // Verifica acceso a Collections (solo con finance.manage)
    canAccessFinancialCollections() {
      if (!this.canAccessFinancialModule) return false;
      if (localStorage.getItem("role") === "admin") return true;
      return this.hasPermission("finance.manage");
    },
    // Verifica acceso a Account Statement (solo con finance.manage)
    canAccessFinancialAccountStatement() {
      if (!this.canAccessFinancialModule) return false;
      if (localStorage.getItem("role") === "admin") return true;
      return this.hasPermission("finance.manage");
    },
    isAccountingModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "accounting_module"
      );
    },
    canAccessAccountingModule() {
      // Primero verificar si el módulo está activo - si no está activo, nadie puede acceder
      if (!this.isAccountingModuleActive) {
        return false;
      }

      // Si el usuario es admin, tiene acceso
      if (localStorage.getItem("role") === "admin") {
        return true;
      }

      // Verificar si el rol del usuario tiene acceso al módulo
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }

      // Buscar en los roles cargados si están disponibles
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes("accounting_module");
        }
      }

      // Lógica de respaldo para roles específicos
      const allowedRoles = ["admin", "employee"];
      return allowedRoles.includes(userRole);
    },
    isDHLModuleActive() {
      return this.activeModules.some((module) => module.name === "dhl_module");
    },
    canAccessDHLModule() {
      // Primero verificar si el módulo está activo - si no está activo, nadie puede acceder
      if (!this.isDHLModuleActive) {
        return false;
      }

      // Si el usuario es admin, tiene acceso
      if (localStorage.getItem("role") === "admin") {
        return true;
      }

      // Verificar si el rol del usuario tiene acceso al módulo
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }

      // Buscar en los roles cargados si están disponibles
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes("dhl_module");
        }
      }

      // Lógica de respaldo para roles específicos
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isFindTeuModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "findTeu_module"
      );
    },
    canAccessFindTeuModule() {
      // Primero verificar si el módulo está activo - si no está activo, nadie puede acceder
      if (!this.isFindTeuModuleActive) {
        return false;
      }

      // Si el usuario es admin, tiene acceso
      if (localStorage.getItem("role") === "admin") {
        return true;
      }

      // Verificar si el rol del usuario tiene acceso al módulo
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }

      // Buscar en los roles cargados si están disponibles
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes("findTeu_module");
        }
      }

      // Lógica de respaldo para roles específicos
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isComexstatsModuleActive() {
      return this.activeModules.some(
        (module) => module.name === COMEXSTATS_MODULE_NAME
      );
    },
    canAccessComexstatsModule() {
      if (!this.isComexstatsModuleActive) {
        return false;
      }
      if (localStorage.getItem("role") === "admin") {
        return true;
      }
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(COMEXSTATS_MODULE_NAME);
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isHistoryModuleActive() {
      return this.activeModules.some(
        (module) => module.name === HISTORY_MODULE_NAME
      );
    },
    canAccessHistoryModule() {
      if (!this.isHistoryModuleActive) {
        return false;
      }
      if (localStorage.getItem("role") === "admin") {
        return true;
      }
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(HISTORY_MODULE_NAME);
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isFobtradingModuleActive() {
      return this.activeModules.some(
        (module) => module.name === FOBTRADING_MODULE_NAME
      );
    },
    canAccessFobtradingModule() {
      if (!this.isFobtradingModuleActive) {
        return false;
      }
      if (localStorage.getItem("role") === "admin") {
        return true;
      }
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(FOBTRADING_MODULE_NAME);
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isMevModuleActive() {
      return this.activeModules.some(
        (module) => module.name === MEV_MODULE_NAME
      );
    },
    canAccessMevModule() {
      if (!this.isMevModuleActive) {
        return false;
      }
      if (localStorage.getItem("role") === "admin") {
        return true;
      }
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(MEV_MODULE_NAME);
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isOutlookModuleActive() {
      return this.activeModules.some(
        (module) => module.name === OUTLOOK_MODULE_NAME
      );
    },
    canAccessOutlookModule() {
      if (!this.isOutlookModuleActive) {
        return false;
      }
      if (localStorage.getItem("role") === "admin") {
        return true;
      }
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(OUTLOOK_MODULE_NAME);
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isNotaComercialModuleActive() {
      return this.activeModules.some(
        (module) => module.name === NOTA_COMERCIAL_MODULE_NAME
      );
    },
    canAccessNotaComercialModule() {
      if (!this.isNotaComercialModuleActive) {
        return false;
      }
      if (localStorage.getItem("role") === "admin") {
        return true;
      }
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(
            NOTA_COMERCIAL_MODULE_NAME
          );
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isAlisofModuleActive() {
      return this.activeModules.some(
        (module) => module.name === ALISOF_MODULE_NAME
      );
    },
    canAccessAlisofModule() {
      if (!this.isAlisofModuleActive) return false;
      return Boolean(localStorage.getItem("role"));
    },
    isBolsaComercialesModuleActive() {
      return this.activeModules.some(
        (module) => module.name === BOLSA_COMERCIALES_MODULE_NAME
      );
    },
    canAccessBolsaComercialesModule() {
      return (
        this.isBolsaComercialesModuleActive && this.bolsaComercialSessionCanAccess
      );
    },
    canAccessBolsaComercialesDashboard() {
      return this.canAccessBolsaComercialesModule;
    },
    canAccessBolsaComercialesSettings() {
      return this.isBolsaComercialesModuleActive && this.isAdminRole;
    },
    isFacturacionAbogadosModuleActive() {
      return this.activeModules.some(
        (module) => module.name === FACTURACION_ABOGADOS_MODULE_NAME
      );
    },
    canAccessFacturacionAbogadosModule() {
      if (!this.isFacturacionAbogadosModuleActive) {
        return false;
      }
      if (localStorage.getItem("role") === "admin") {
        return true;
      }
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(
            FACTURACION_ABOGADOS_MODULE_NAME
          );
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isJurisprudenciaModuleActive() {
      return this.activeModules.some(
        (module) => module.name === JURISPRUDENCIA_MODULE_NAME
      );
    },
    canAccessJurisprudenciaModule() {
      if (!this.isJurisprudenciaModuleActive) {
        return false;
      }
      if (localStorage.getItem("role") === "admin") {
        return true;
      }
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(
            JURISPRUDENCIA_MODULE_NAME
          );
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isArcaModuleActive() {
      return this.activeModules.some((module) => module.name === ARCA_MODULE_NAME);
    },
    /** Datos fiscales y certificado: solo admin o roles con el módulo habilitado (sin rol por defecto). */
    canAccessArcaModule() {
      if (!this.isArcaModuleActive) return false;
      const userRole = localStorage.getItem("role");
      if (userRole === "admin") return true;
      const roleData = (this.userRoles || []).find((role) => role.name === userRole);
      return !!(roleData && (roleData.allowedModules || []).includes(ARCA_MODULE_NAME));
    },
    isAgentChatModuleActive() {
      return this.activeModules.some(
        (module) => module.name === TELEGRAM_AI_MODULE_NAME
      );
    },
    /**
     * Mismo criterio que el back de /chat: módulo activo, rol admin o con el módulo permitido, y el
     * switch «Agente IA» de Funciones del sistema prendido (lo dice /api/agent-chat/status).
     */
    canAccessAgentChat() {
      if (!this.isAgentChatModuleActive || !this.agentChatEnabled) return false;
      const userRole = localStorage.getItem("role");
      if (userRole === "admin") return true;
      const roleData = (this.userRoles || []).find((r) => r.name === userRole);
      return !!(
        roleData &&
        Array.isArray(roleData.allowedModules) &&
        roleData.allowedModules.includes(TELEGRAM_AI_MODULE_NAME)
      );
    },
    isInmobiliariaModuleActive() {
      return this.activeModules.some(
        (module) => module.name === INMOBILIARIA_MODULE_NAME
      );
    },
    canAccessInmobiliariaModule() {
      if (!this.isInmobiliariaModuleActive) {
        return false;
      }
      if (localStorage.getItem("role") === "admin") {
        return true;
      }
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(INMOBILIARIA_MODULE_NAME);
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isExcelMappingModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "excel_mapping_module"
      );
    },
    canAccessExcelMappingModule() {
      if (!this.isExcelMappingModuleActive) return false;
      if (localStorage.getItem("role") === "admin") return true;
      const userRole = localStorage.getItem("role");
      if (!userRole) return false;
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes("excel_mapping_module");
        }
      }
      const allowedRoles = [
        "admin",
        "employee",
        "gym",
        "restaurant",
        "teacher",
      ];
      return allowedRoles.includes(userRole);
    },
    isChatModuleActive() {
      return this.activeModules.some((module) => module.name === "chat_module");
    },
    isCronjobsModuleActive() {
      return this.activeModules.some(
        (module) => module.name === CRONJOBS_MODULE_NAME
      );
    },
    isWebsiteModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "website_module"
      );
    },
    canAccessWebsiteModule() {
      // Primero verificar si el módulo está activo
      if (!this.isWebsiteModuleActive) {
        return false;
      }

      // Si el usuario es admin, tiene acceso
      if (localStorage.getItem("role") === "admin") {
        return true;
      }

      // Verificar si el rol del usuario tiene acceso al módulo
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }

      // Buscar en los roles cargados si están disponibles
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes("website_module");
        }
      }

      // Lógica de respaldo para roles específicos
      const allowedRoles = ["admin", "employee"];
      return allowedRoles.includes(userRole);
    },
    isEmailModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "email_module"
      );
    },
    canAccessTranslationsModule() {
      if (localStorage.getItem("role") === "admin") return true;
      return (
        this.hasPermission("translation_view") ||
        this.hasPermission("translation_edit")
      );
    },
    canAccessEmailModule() {
      // Primero verificar si el módulo está activo
      if (!this.isEmailModuleActive) {
        return false;
      }

      // Si el usuario es admin, tiene acceso
      const userRole = localStorage.getItem("role");
      if (userRole === "admin") {
        return true;
      }

      // Verificar si el rol del usuario tiene acceso al módulo
      if (!userRole) {
        return false;
      }

      // Buscar en los roles cargados si están disponibles
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );

        // Si encontramos el rol, verificar allowedModules
        if (userRoleData) {
          // Si allowedModules está definido (incluso si es array vacío), respetarlo estrictamente
          if (userRoleData.hasOwnProperty("allowedModules")) {
            // Si es un array, verificar si incluye el módulo
            if (Array.isArray(userRoleData.allowedModules)) {
              return userRoleData.allowedModules.includes("email_module");
            }
            // Si no es un array pero está definido, denegar acceso
            return false;
          }
          // Si allowedModules NO está definido, usar lógica de respaldo
        }
      }

      // Lógica de respaldo solo si allowedModules NO está definido
      const allowedRoles = ["admin", "employee"];
      return allowedRoles.includes(userRole);
    },
    isStockModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "stock_module"
      );
    },
    canAccessStockModule() {
      // Primero verificar si el módulo está activo
      if (!this.isStockModuleActive) {
        return false;
      }

      // Si el usuario es admin, tiene acceso
      if (localStorage.getItem("role") === "admin") {
        return true;
      }

      // Verificar si el rol del usuario tiene acceso al módulo
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }

      // Buscar en los roles cargados si están disponibles
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes("stock_module");
        }
      }

      // Lógica de respaldo para roles específicos
      const allowedRoles = ["admin", "employee"];
      return allowedRoles.includes(userRole);
    },
    isSupportModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "support_module"
      );
    },
    canAccessSupportModule() {
      // Primero verificar si el módulo está activo
      if (!this.isSupportModuleActive) {
        return false;
      }

      // En tenants *-test el soporte está deshabilitado (tickets solo en prod)
      if (isTenantTestEnvironment()) {
        return false;
      }

      // Support module is accessible to all authenticated users
      // (users can create tickets, admins can manage them)
      return true;
    },
    isInstanceBuilderModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "instance_builder_module"
      );
    },
    canAccessInstanceBuilderModule() {
      // Primero verificar si el módulo está activo
      if (!this.isInstanceBuilderModuleActive) {
        return false;
      }

      // Si el usuario es admin, tiene acceso
      if (localStorage.getItem("role") === "admin") {
        return true;
      }

      // Verificar si el rol del usuario tiene acceso al módulo
      const userRole = localStorage.getItem("role");
      if (!userRole) {
        return false;
      }

      // Buscar en los roles cargados si están disponibles
      if (this.userRoles && this.userRoles.length > 0) {
        const userRoleData = this.userRoles.find(
          (role) => role.name === userRole
        );
        if (userRoleData && userRoleData.allowedModules) {
          return userRoleData.allowedModules.includes(
            "instance_builder_module"
          );
        }
      }

      // Solo admins pueden acceder al Instance Builder por defecto
      const allowedRoles = ["admin"];
      return allowedRoles.includes(userRole);
    },
    isTelegramAiModuleActive() {
      return this.activeModules.some(
        (module) => module.name === "telegram_ai_module"
      );
    },
    canAccessTelegramAiModule() {
      if (localStorage.getItem("role") !== "admin") {
        return false;
      }
      return true;
    },
  },
  async created() {
    const response = await wencesApi.get("/api/settings");

    this.site = response.data.data;
  },
  data() {
    return {
      /** Agente IA disponible para este usuario (ver fetchAgentChatEnabled). */
      agentChatEnabled: false,
      models: [],
      reports: [],
      userName: "",
      sidebarBackgroundColor: "black",
      site: false,
      activeModules: [],
      userRoles: [],
      cronjobsModuleMenuItem,
      comexstatsModuleMenuItem,
      historyModuleMenuItem,
      fobtradingModuleMenuItem,
      mevModuleMenuItem,
      outlookModuleMenuItem,
      notaComercialModuleMenuItem,
      alisofModuleMenuItem,
      alisofPosicionesMenuItem,
      bolsaComercialesModuleMenuItem,
      facturacionAbogadosModuleMenuItem,
      jurisprudenciaModuleMenuItem,
      arcaModuleMenuItem,
      agentChatMenuItem,
      inmobiliariaModuleMenuItem,
      userMenuFolders: [],
      userMenuLayoutMerged: null,
      bolsaComercialIsConfiguredUser: false,
      bolsaComercialSessionCanAccess: false,
      supportUnreadMentions: 0,
      supportUnreadMentionTicketId: null,
      supportUnreadMentionsFetchedAt: 0,
      supportUnreadMentionsTimer: null,
      supportUnreadMentionsInterval: null,
    };
  },
  methods: {
    menuBranchKey(br, idx) {
      if (br && br.kind === "builtin" && br.ref) {
        return String(br.ref).replace(/[^a-zA-Z0-9_-]/g, "_");
      }
      if (br && br.kind === "customLink") {
        const id = (br.cid || br.href || "").trim();
        return (
          "cu_" +
          id.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 96)
        );
      }
      if (br && br.kind === "folder" && br.id) {
        return "f_" + br.id;
      }
      return "i_" + idx;
    },
    /**
     * Alta desde menú ("Add new"): mismo criterio que editar — permiso de gestión {model}.manage.
     */
    canAddDynamicModel(modelName) {
      if (!modelName) return false;
      if (
        typeof localStorage !== "undefined" &&
        localStorage.getItem("role") === "admin"
      ) {
        return true;
      }
      const m = String(modelName).toLowerCase();
      return this.hasPermission(`${m}.manage`);
    },
    async fetchUserMenuLayout() {
      try {
        const { data } = await wencesApi.get("/api/users/menu-editor-state");
        if (data && data.ok) {
          this.userMenuFolders = data.data.folders || [];
          const roots =
            data.data.layout && Array.isArray(data.data.layout.roots)
              ? data.data.layout.roots
              : [];
          const seed = roots.length > 0 ? roots : this.defaultMenuRawRoots;
          this.userMenuLayoutMerged = mergeMissingBuiltins(
            seed,
            this.defaultMenuRawRoots
          );
        }
      } catch (e) {
        console.warn("Could not load user menu layout", e);
        this.userMenuFolders = [];
        this.userMenuLayoutMerged = null;
      }
    },
    hasElement(className) {
      return document.getElementsByClassName(className).length > 0;
    },

    toggleSidebar() {
      this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
    },
    closeSidebarIfOpen() {
      if (this.$sidebar.showSidebar) this.$sidebar.displaySidebar(false);
    },
    onMobileBottomNav(item) {
      if (item.key === "menu") {
        this.toggleSidebar();
        return;
      }
      if (this.$sidebar.showSidebar) this.$sidebar.displaySidebar(false);
      if (item.to && this.$route.path !== item.to) this.$router.push(item.to);
    },
    minimizeSidebar() {
      if (this.$sidebar) {
        this.$sidebar.toggleMinimize();
      }
    },
    async fetchModels() {
      try {
        const { data } = await wencesApi.get(
          "/api/gotrading/model-definitions"
        );
        this.models = data;
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    },
    async fetchReports() {
      try {
        const { data } = await wencesApi.get("/api/gotrading/reports");
        this.reports = data || [];
      } catch (error) {
        console.error("Error fetching reports:", error);
        this.reports = [];
      }
    },
    getModelReports(modelName) {
      return reportsOfModel(this.reports, modelName, (groupModel) =>
        this.hasPermission(`${groupModel}.view`)
      );
    },
    /** Campos tipo group del modelo con showInMenu y con permiso de vista para el listado del grupo */
    getGroupFieldsInMenu(model) {
      if (!model || !model.fields || !Array.isArray(model.fields)) return [];
      return model.fields
        .filter(
          (f) =>
            f.type === "group" &&
            f.showInMenu &&
            !f.hide &&
            this.hasPermission(
              `${(model.modelName + "_" + f.key).toLowerCase()}.view`
            )
        )
        .map((f) => ({
          key: f.key,
          label: f.label || f.key,
          modelName: `${model.modelName}_${f.key}`,
        }));
    },
    async fetchSupportUnreadMentions() {
      if (!this.canAccessSupportModule) {
        this.supportUnreadMentions = 0;
        this.supportUnreadMentionTicketId = null;
        return;
      }
      this.supportUnreadMentionsFetchedAt = Date.now();
      try {
        const { data } = await wencesApi.get("/api/support/unread-mentions");
        this.supportUnreadMentions = Number(data?.data?.count) || 0;
        this.supportUnreadMentionTicketId = data?.data?.ticket?._id || null;
      } catch (e) {
        // El badge es secundario: si falla, el menú sigue igual
      }
    },
    /** Al marcar comentarios como leídos llegan varios eventos seguidos (hover). */
    scheduleSupportUnreadMentionsFetch() {
      clearTimeout(this.supportUnreadMentionsTimer);
      this.supportUnreadMentionsTimer = setTimeout(
        this.fetchSupportUnreadMentions,
        1500
      );
    },
    refreshSupportUnreadMentionsIfStale() {
      if (document.hidden) return;
      if (Date.now() - this.supportUnreadMentionsFetchedAt < 60 * 1000) return;
      this.fetchSupportUnreadMentions();
    },
    /** El Agente IA para este usuario: módulo, acceso y switch de Funciones del sistema. */
    async fetchAgentChatEnabled() {
      if (!this.isAgentChatModuleActive) {
        this.agentChatEnabled = false;
        return;
      }
      try {
        const { data } = await wencesApi.get("/api/agent-chat/status");
        this.agentChatEnabled = !!(data && data.available);
      } catch (e) {
        this.agentChatEnabled = false;
      }
    },
    async fetchActiveModules() {
      try {
        const response = await wencesApi.get("/api/modules/active");

        // Intentar diferentes formatos de respuesta
        if (response.data && Array.isArray(response.data)) {
          this.activeModules = response.data;
        } else if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          this.activeModules = response.data.data;
        } else {
          this.activeModules = [];
        }

        // Guardar en localStorage para el router guard
        localStorage.setItem(
          "activeModules",
          JSON.stringify(this.activeModules)
        );

        await this.fetchBolsaComercialesSession();
        this.fetchAgentChatEnabled();
      } catch (error) {
        console.error("Error fetching active modules:", error);
        this.activeModules = [];
        localStorage.setItem("activeModules", JSON.stringify([]));
      }
    },

    async fetchBolsaComercialesSession() {
      this.bolsaComercialIsConfiguredUser = false;
      this.bolsaComercialSessionCanAccess = false;
      if (!this.isBolsaComercialesModuleActive) {
        return;
      }
      try {
        const res = await wencesApi.get("/api/bolsa-comerciales/session");
        const data = res.data?.data || {};
        this.bolsaComercialIsConfiguredUser = !!data.isCommercial;
        this.bolsaComercialSessionCanAccess = !!data.canAccess;
      } catch (error) {
        this.bolsaComercialIsConfiguredUser = false;
        this.bolsaComercialSessionCanAccess = false;
      }
    },

    async fetchUserRoles() {
      try {
        // Obtener el rol REAL del usuario desde el backend para asegurar que es correcto
        try {
          const profileResponse = await wencesApi.get("/api/users/myprofile");
          if (
            profileResponse.data &&
            profileResponse.data.ok &&
            profileResponse.data.data
          ) {
            const userData = profileResponse.data.data;
            // Obtener el nombre del rol
            if (typeof userData.role === "string") {
              // Si es un ID, necesitamos obtener el rol
              const roleResponse = await wencesApi.get(
                `/api/roles/${userData.role}`
              );
              if (roleResponse.data && roleResponse.data.data) {
                const actualUserRole = roleResponse.data.data.name;
                // Actualizar localStorage con el rol real del backend
                localStorage.setItem("role", actualUserRole);
                this.$root.$emit("gotrading-user-role-updated");
              }
            } else if (userData.role && userData.role.name) {
              // Actualizar localStorage con el rol real del backend
              localStorage.setItem("role", userData.role.name);
              this.$root.$emit("gotrading-user-role-updated");
            }
          }
        } catch (error) {
          console.error("Error obteniendo perfil del usuario:", error);
        }

        // Obtener todos los roles
        const response = await wencesApi.get("/api/roles");
        if (response.data && response.data.data) {
          this.userRoles = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching user roles:", error);
        this.userRoles = [];
      }
    },

    async hasModuleAccess(moduleName) {
      try {
        // Si el usuario es admin, tiene acceso a todos los módulos
        if (localStorage.getItem("role") === "admin") {
          return true;
        }

        // Verificar si el módulo está activo
        const isModuleActive = this.activeModules.some(
          (module) => module.name === moduleName
        );
        if (!isModuleActive) {
          return false;
        }

        // Obtener el rol del usuario desde localStorage
        const userRole = localStorage.getItem("role");
        if (!userRole) {
          return false;
        }

        // Obtener información del rol desde el backend para verificar módulos permitidos
        const response = await wencesApi.get(`/api/roles`);
        if (response.data && response.data.data) {
          const userRoleData = response.data.data.find(
            (role) => role.name === userRole
          );
          if (userRoleData && userRoleData.allowedModules) {
            return userRoleData.allowedModules.includes(moduleName);
          }
        }

        // Si no se puede verificar desde el backend, usar lógica de respaldo
        const allowedRoles = [
          "admin",
          "employee",
          "gym",
          "restaurant",
          "teacher",
        ];
        return allowedRoles.includes(userRole);
      } catch (error) {
        console.error("Error verificando acceso al módulo:", error);
        // En caso de error, usar lógica de respaldo
        const userRole = localStorage.getItem("role");
        const allowedRoles = [
          "admin",
          "employee",
          "gym",
          "restaurant",
          "teacher",
        ];
        return allowedRoles.includes(userRole);
      }
    },
    async handleReportCreated() {
      await this.fetchReports();
      await this.fetchUserMenuLayout();
    },
    updatePageTitle(route) {
      const path = route && route.path ? route.path : "";
      const isPrettyItemView = path.startsWith("/i/");
      // Solo actualizar el título para rutas de listado de dynamic-model
      // No actualizar para rutas de detalle (view/:itemId) ya que las maneja dynamic-model-detail.vue
      if (path.startsWith("/dynamic-model/") || isPrettyItemView) {
        if (isPrettyItemView) {
          return;
        }
        const pathParts = path.split("/");

        // Solo actualizar si es una ruta de listado (no tiene /view/ o /edit/ en el path)
        // Formato de listado: /dynamic-model/:modelName
        // Formato de detalle: /dynamic-model/:modelName/view/:itemId
        // Formato de edición: /dynamic-model/:modelName/edit/:itemId
        if (
          pathParts.length >= 3 &&
          !pathParts.includes("view") &&
          !pathParts.includes("edit") &&
          !pathParts.includes("display")
        ) {
          const modelName = pathParts[2];

          // Buscar el modelo en la lista de modelos cargados
          const model = this.models.find(
            (m) => m.modelName.toLowerCase() === modelName.toLowerCase()
          );

          if (model && this.site && this.site.siteName) {
            const modelLabel = model.label
              ? this.$t(model.label)
              : this.$t(model.modelName);
            const lastSegment = pathParts[pathParts.length - 1];
            let listSuffix = "";
            if (lastSegment === "trash") {
              listSuffix = ` - ${this.$t("listTitleTrash")}`;
            } else if (lastSegment === "archive") {
              listSuffix = ` - ${this.$t("listTitleArchive")}`;
            }
            document.title = `${modelLabel}${listSuffix} | ${this.site.siteName}`;
            return;
          }
        }
      }

      // Si no es una ruta de dynamic-model listado, mantener el título por defecto
      // (las rutas de detalle manejan su propio título)
      if (
        this.site &&
        this.site.siteName &&
        (!route || !route.path || (!route.path.startsWith("/dynamic-model/") && !route.path.startsWith("/i/")))
      ) {
        document.title = this.site.siteName;
      }
    },
  },
  async mounted() {
    this.userName = localStorage.getItem("name") || "Anonymous";
    await Promise.all([
      this.fetchModels(),
      this.fetchReports(),
      this.fetchActiveModules(),
      this.fetchUserRoles(),
    ]);
    await this.fetchUserMenuLayout();
    this.fetchSupportUnreadMentions();
    this.supportUnreadMentionsInterval = setInterval(
      this.refreshSupportUnreadMentionsIfStale,
      5 * 60 * 1000
    );

    if (this.userName !== "Anonymous") {
      const userName =
        this.userName + " - " + localStorage.getItem("restaurant");
    }
    this.$root.$on("user-menu-layout-saved", this.fetchUserMenuLayout);
    this.$root.$on("gotrading-report-created", this.handleReportCreated);
    this.$root.$on(
      "support-read-state-changed",
      this.scheduleSupportUnreadMentionsFetch
    );
  },
  beforeDestroy() {
    this.$root.$off("user-menu-layout-saved", this.fetchUserMenuLayout);
    this.$root.$off("gotrading-report-created", this.handleReportCreated);
    this.$root.$off(
      "support-read-state-changed",
      this.scheduleSupportUnreadMentionsFetch
    );
    clearTimeout(this.supportUnreadMentionsTimer);
    clearInterval(this.supportUnreadMentionsInterval);
  },
  watch: {
    sidebarMini() {
      this.minimizeSidebar();
    },
    $route: {
      immediate: true,
      handler(to, from) {
        this.updatePageTitle(to);
        if (from) this.refreshSupportUnreadMentionsIfStale();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.alert {
  margin-bottom: 1px !important;
}
.sidebar .credits .md-icon.md-theme-default.md-icon-font,
.credits i {
  font-size: 10px !important;
  color: var(--bs-primary) !important;
  margin: 0;
}

.credits {
  float: left;
  margin-left: 28px;
  font-size: 11px;
  margin-top: -5px;
  color: var(--bs-primary) !important;
  overflow: hidden;
  height: 19px;
}
.md-button.md-info.md-simple i {
  color: #424242 !important;
}
</style>
<style lang="scss">
.sidebar.mobile {
  overflow: hidden !important;
}
.md-field:before {
  background-color: none !important;
  background: none !important;
}

.sidebar-mobile i {
  font-size: 35px !important;
  margin-right: 17px;
  margin-bottom: 11px;
}

.sidebar.mobile {
  position: relative;
  float: left;
  width: 100% !important;
  overflow-y: auto;
  right: auto;
  top: auto;
  left: auto;
  height: 67px;
  transform: none;
}

.image-container {
  max-width: 400px;
}

.error-message {
  color: red;
  margin-top: 10px;
}

.sidebar-mini .sidebar {
  z-index: 100 !important;
}

.sidebar.mobile {
  display: none; // Oculto por defecto

  @media (max-width: 980px) {
    display: block; // Mostrar solo en pantallas pequeñas
  }
}

.menu-container {
  margin-right: 10px;
  cursor: pointer;
}
.menu-language {
  padding: 0px;
}
.menu-language.md-field input {
  border-bottom: 0 !important;
}

.md-select {
  color: white !important;
}

.menu-toggle {
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 1000;
  cursor: pointer;
  font-size: 24px;
  display: none; // Oculto por defecto

  @media (max-width: 768px) {
    display: block; // Mostrar solo en pantallas pequeñas
  }
}

.menu-toggle i {
  color: #fff;
}

.nav-open .menu-toggle i {
  color: #000;
}

.language-select {
  margin: 10px;
}

.sidebar .sidebar-wrapper .md-field.md-theme-default.md-focused .md-input,
.sidebar .sidebar-wrapper .md-field.md-theme-default.md-focused .md-textarea,
.sidebar .sidebar-wrapper .md-field.md-theme-default.md-has-value .md-input,
.sidebar
  .sidebar-wrapper
  .md-field.md-theme-default.md-has-value
  .md-textarea
  .md-field.md-theme-default.md-has-value
  .md-input,
.sidebar .sidebar-wrapper .md-field .md-input {
  -webkit-text-fill-color: white !important;
}
.sidebar .sidebar-wrapper .md-field .md-input,
.sidebar .sidebar-wrapper .md-field .md-textarea {
  -webkit-text-fill-color: white !important;
}

.sidebar .sidebar-wrapper .md-icon.md-theme-default.md-icon-image svg {
  fill: white !important;
}

/* Encabezado del teléfono (isMobileLayout). */
.m-header {
  /* No es sticky: la barra de cada pantalla (MobileAppBar) es la que queda fija. */
  position: relative;
  z-index: 25;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.75rem;
  background: var(--m-surface);
  border-bottom: 1px solid var(--m-border);
}
.m-header__logo {
  flex-shrink: 0;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  overflow: hidden;
  background: var(--m-surface-2);
  border: 1px solid var(--m-border);
}
.m-header__logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.m-header__search {
  flex: 1;
  min-width: 0;
}
.m-header__menu {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--m-text);
}
.m-header__menu:active {
  background: var(--m-pressed);
}
.m-header__menu i {
  font-size: 1.75rem;
  color: var(--m-text) !important;
}

.logotel {
  margin-left: 9px;
  margin-top: -4px;
  float: left;
}

.language-select select {
  padding: 5px;
  border-radius: 4px;
  border: 1px solid #ccc;
}
</style>

<style>
.btn-primary,
.md-button.md-primary,
.md-button.md-primary:hover,
.md-button.md-primary:focus,
.md-button.md-primary:active,
.md-button.md-primary.active,
.md-button.md-primary:active:focus,
.md-button.md-primary:active:hover,
.md-button.md-primary.active:focus,
.md-button.md-primary.active:hover,
.open > .md-button.md-primary.dropdown-toggle,
.open > .md-button.md-primary.dropdown-toggle:focus,
.open > .md-button.md-primary.dropdown-toggle:hover {
  background-color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
}

.btn-outline-primary,
.btn-outline-primary:hover,
.btn-outline-primary:focus,
.btn-outline-primary:active,
.btn-outline-primary.active {
  color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
  background-color: transparent !important;
}
.btn-outline-primary:hover,
.btn-outline-primary:focus,
.btn-outline-primary:active,
.btn-outline-primary.active {
  color: #fff !important;
  background-color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
}

.subitem {
  padding-left: 13px !important;
}

.text-primary {
  color: var(--bs-primary) !important;
}
.badge.badge-primary {
  background-color: var(--bs-primary) !important;
}

/* Sidebar active item (only .active, not .router-link-active): use primary color from settings branding */
.sidebar li.active > a:not([data-toggle="collapse"]),
.off-canvas-sidebar li.active > a:not([data-toggle="collapse"]) {
  background-color: var(--bs-primary) !important;
  color: #fff !important;
  box-shadow: none;
}
.sidebar li.active > a:not([data-toggle="collapse"]) i,
.sidebar li.active > a:not([data-toggle="collapse"]) .fa,
.off-canvas-sidebar li.active > a:not([data-toggle="collapse"]) i {
  color: #fff !important;
  opacity: 1;
}
.sidebar .nav li {
  width: 100% !important;
}
</style>

<style scoped>
/* Breadcrumb y el icono de la IA en la misma línea (pantallas de modelos, escritorio). */
.breadcrumb-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0.5rem;
}
.breadcrumb-row__crumbs {
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 0 !important;
}
.breadcrumb-row__ai {
  flex-shrink: 0;
  margin-left: auto;
  margin-right: 12px;
}

.marquee-container {
  overflow: hidden;
  width: 100%;
  white-space: nowrap;
}

.marquee {
  display: flex;
  width: max-content;
  animation: marquee-scroll 60s linear infinite;
}

.marquee-content {
  display: flex;
}

.news-item {
  padding: 0 20px;
}

/* Animación de desplazamiento */
@keyframes marquee-scroll {
  from {
    transform: translateX(0%);
  }
  to {
    transform: translateX(-50%);
  }
}
</style>
